import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Crown } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentReturn({ mode = "success" }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth() || {};
  const [state, setState] = useState(mode === "cancel" ? "cancel" : "checking");
  const tries = useRef(0);

  useEffect(() => {
    if (mode === "cancel") return;
    const sid = params.get("session_id");
    if (!sid) { setState("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const r = await api.stripeStatus(sid);
        if (r.payment_status === "paid") { await refresh?.(); setState("paid"); return; }
        if (["failed", "expired"].includes(r.payment_status)) { setState("failed"); return; }
      } catch { /* keep polling */ }
      if (tries.current++ < 20) timer = setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
    return () => clearTimeout(timer);
  }, [mode, params, refresh]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A] px-5 text-white" data-testid="payment-return">
      <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-white/[0.02] p-8 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto mb-4 animate-spin text-[#00F0FF]" size={34} />
            <div className="font-display text-xl font-black">Confirming your payment…</div>
            <p className="mt-2 text-sm text-[#A1A1AA]">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto mb-4 text-[#22c55e]" size={40} />
            <div className="font-display text-2xl font-black" data-testid="payment-success">Pro activated 🎉</div>
            <p className="mt-2 text-sm text-[#A1A1AA]">You now have 28 days of Caption Motion Pro.</p>
            <button data-testid="go-profile-btn" onClick={() => navigate("/profile")}
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#00F0FF] py-3 text-sm font-black text-black">
              <Crown size={16} /> View my plan
            </button>
          </>
        )}
        {(state === "cancel" || state === "failed" || state === "timeout" || state === "error") && (
          <>
            <XCircle className="mx-auto mb-4 text-[#f87171]" size={40} />
            <div className="font-display text-xl font-black" data-testid="payment-cancel">
              {state === "cancel" ? "Payment cancelled" : state === "timeout" ? "Still processing" : "Payment not completed"}
            </div>
            <p className="mt-2 text-sm text-[#A1A1AA]">
              {state === "timeout" ? "If you were charged, your Pro will activate shortly." : "No charge was made. You can try again anytime."}
            </p>
            <Link to="/pricing" data-testid="back-pricing-btn"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/15 py-3 text-sm font-bold text-white hover:border-white/30">
              Back to pricing
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
